import React from 'react';
import { FaBed, FaBath, FaHome, FaTimesCircle } from 'react-icons/fa';

export default function OffMarketModal({ listing, details, onClose }) {
  // Determine if we're still loading details (i.e. details is null or details is missing its address)
  const isOffMarket = !listing.formattedAddress;
  const isLoading = isOffMarket && (!details || !details.address);
  // Choose a smaller modal style when loading; otherwise, use the full-size modal
  const modalContent = isLoading
    ? {
        background: '#fff',
        borderRadius: '15px',
        width: '300px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        padding: '20px',
        textAlign: 'center',
        position: 'relative',
        animation: 'fadeIn 0.3s ease-out',
      }
    : {
        background: '#fff',
        borderRadius: '15px',
        width: '90%',
        maxWidth: '600px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
        position: 'relative',
        animation: 'fadeIn 0.3s ease-out',
      };

  // Common inline styles
  const modalOverlay = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const closeButton = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'transparent',
    border: 'none',
    fontSize: '28px',
    lineHeight: 1,
    cursor: 'pointer',
    color: '#fff',
    zIndex: 10,
  };

  // Styles used when details are loaded
  const headerStyle = {
    width: '100%',
    height: '250px',
    overflow: 'hidden',
    position: 'relative',
  };

  const propertyImageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  const placeholderImageStyle = {
    width: '100%',
    height: '100%',
    backgroundColor: '#ddd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '60px',
    color: '#888',
  };

  const modalBody = {
    padding: '20px',
  };

  const modalTitle = {
    margin: '0 0 10px',
    fontSize: '26px',
    color: '#333',
    textAlign: 'center',
  };

  const modalText = {
    margin: '10px 0',
    fontSize: '16px',
    color: '#555',
  };

  const iconStyle = {
    marginRight: '5px',
    color: '#007aff',
  };

  const contactSection = {
    display: 'flex',
    alignItems: 'center',
    margin: '15px 0',
  };

  const realtorImageStyle = {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    marginRight: '15px',
    objectFit: 'cover',
    border: '2px solid #007aff',
  };

  const descriptionStyle = {
    marginTop: '15px',
    lineHeight: 1.5,
  };

  const modalFooter = {
    padding: '10px 20px',
    textAlign: 'right',
    borderTop: '1px solid #eee',
  };

  const modalCloseButton = {
    backgroundColor: '#007aff',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'background-color 0.2s ease',
  };

  // Loading styles
  const spinnerStyle = {
    fontSize: '50px',
    color: '#007aff',
    animation: 'spin 1s linear infinite',
    marginBottom: '10px',
  };

  const loadingTextStyle = {
    fontSize: '18px',
    color: '#555',
  };

  // Sold message style
  const soldMessageStyle = {
    textAlign: 'center',
    padding: '40px 0',
    color: 'blue',
    fontSize: '18px',
    fontWeight: 'bold',
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent} onClick={e => e.stopPropagation()}>
        {/* Show close button only in full-size (non-loading) mode */}
        {!isLoading && (
          <button style={closeButton} onClick={onClose}>
            ×
          </button>
        )}
        {isLoading ? (
          // Loading state: compact modal with just spinner and text
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div style={spinnerStyle}>
              <FaHome />
            </div>
            <p style={loadingTextStyle}>Loading details...</p>
          </div>
        ) : details.sold ? (
          // Sold state
          <div style={soldMessageStyle}>
            <p>Off-Market Property Details Coming Soon</p>
          </div>
        ) : (
          // Full details state
          <>
            <div style={headerStyle}>
              {details.image ? (
                <img src={details.image} alt="Property" style={propertyImageStyle} />
              ) : (
                <div style={placeholderImageStyle}>
                  <FaHome />
                </div>
              )}
            </div>
            <div style={modalBody}>
              {/* Centered Address Row */}
              <p style={{ ...modalText, textAlign: 'center' }}>
                {details.address}
              </p>
              {/* SQFT, Beds, and Baths in one row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', flex: '1', justifyContent: 'center' }}>
                  <span style={modalText}><strong>SQFT:</strong> {details.sqft}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', flex: '1', justifyContent: 'center' }}>
                <FaBed style={iconStyle} />
                  <span style={modalText}><strong>Beds:</strong> {details.beds}</span>
                  
                </div>
                <div style={{ display: 'flex', alignItems: 'center', flex: '1', justifyContent: 'center' }}>
                <FaBath style={iconStyle} />
                  <span style={modalText}><strong>Baths:</strong> {details.baths}</span>
                 
                </div>
              </div>
              <div style={contactSection}>
                {details.realtorImage && (
                  <img src={details.realtorImage} alt="Realtor" style={realtorImageStyle} />
                )}
                <div>
                  <p style={modalText}><strong>Wholeseller:</strong> {details.wholesellerName}</p>
                  <p style={modalText}><strong>Wholesale Company:</strong> {details.wholesaleCompany}</p>
                </div>
              </div>
              <p style={{ ...modalText, ...descriptionStyle }}>
                <strong>Description:</strong> {details.description}
              </p>
            </div>
            <div style={modalFooter}>
              <button style={modalCloseButton} onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
        `}
      </style>
    </div>
  );
}
